import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { useRef, useState } from 'react';
import Barchart from 'components/molecules/Barchart';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import Loader from 'components/molecules/Loader';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { calculateContainerHeight, formatNumWithKMBAlt, scaleMaxValue } from 'utils/helperMethods';
import useDecompositionWaterfallData from './hooks/useDecompositionWaterfallData';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { isEmpty } from 'lodash';
import WaterFallChartIndividualised from 'components/molecules/WaterFallChartIndividualised';

const DecompositionWaterfallChart = ({ isWaterfallChartEnabled = true }: { isWaterfallChartEnabled?: boolean }) => {
    const chartRef = useRef(null);
    const chartHeight = calculateContainerHeight(2.3);

    const [toLeft] = useState(0);

    const containerWidth = useContainerWidth({
        containerId: 'decomposition-waterfall-infinite-scroll',
        buffer: 300,
    });

    const {
        waterFallChartData,
        currentYearData,
        previousYearData,
        fetchNextPage,
        isFetchingNextPage,
        min: waterfallMin,
        max: waterfallMax,
    } = useDecompositionWaterfallData(isWaterfallChartEnabled);

    const minGraphWidth = 100;
    const currentDataLength = waterFallChartData?.length ?? 0;
    const currentContainerWidth = containerWidth;

    const currentGraphWidth =
        currentContainerWidth / currentDataLength < minGraphWidth
            ? minGraphWidth
            : currentContainerWidth / currentDataLength;

    return (
        <div>
            <div className="items-center flex flex-row justify-between my-2 mb-4">
                <div className="text-sm">
                    <p>{'Profit Growth'}</p>
                </div>
                <div className="flex flex-row gap-2 text-xs">
                    <div className="w-3 h-2 self-center bg-[#5F49BC]" />
                    <p>{'Current and Last Year'}</p>
                    <div className="w-3 h-2 self-center bg-[#42A531]" />
                    <p>{'Positive Growth'}</p>
                    <div className="w-3 h-2 self-center bg-[#CA3D3D]" />
                    <p>{'Negative Growth'}</p>
                </div>
                <div />
            </div>

            {/* Chart */}
            <div className="flex mt-2 flex-row">
                {/* Y Axis */}
                <div className="flex flex-col">
                    <Barchart
                        padding={[0, 0, -2, 0]}
                        barWidth={40}
                        xAxisConfig={false}
                        xAxisLabelFormatter={(val) => {
                            return '';
                        }}
                        dataset={[]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`decomposition-waterfall-y-axis`}
                        fields={['brand_name', 'totalProfit', 'type']}
                        minBound={waterfallMin}
                        maxBound={scaleMaxValue(20, waterfallMax)}
                        width={50}
                        colorKey={'type'}
                        yAxisConfig={true}
                        YGrid={false}
                        yAxisLabelFormatter={(val: any) => {
                            return formatNumWithKMBAlt(val);
                        }}
                    />
                </div>
                {/* Last Year Chart */}
                <div className="flex flex-col">
                    <Barchart
                        padding={[0, 0, -8, 0]}
                        barWidth={40}
                        xAxisConfig={true}
                        xAxisLabelFormatter={(val) => {
                            return '';
                        }}
                        dataset={[
                            {
                                brand_name: 'Year Ago',
                                totalProfitGrowth: previousYearData ?? 0,
                                type: '#5F49BC',
                            },
                        ]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`decomposition-waterfall-previous-year`}
                        fields={['brand_name', 'totalProfitGrowth', 'type']}
                        minBound={waterfallMin}
                        maxBound={scaleMaxValue(20, waterfallMax)}
                        width={100}
                        colorKey={'type'}
                        yAxisConfig={true}
                        yAxisLabelFormatter={(val: any) => {
                            return '';
                        }}
                        tooltipFormatter={(xAxis, val) => {
                            return {
                                name: 'Profit Growth',
                                value: formatNumWithKMBAlt(val),
                            };
                        }}
                    />
                    <p className="text-center">{'Year Ago'}</p>
                </div>

                {/* WaterFall Chart */}
                <HorizontalInfiniteScroller
                    id={`decomposition-waterfall-infinite-scroll`}
                    hasMore={true}
                    scrollThreshold={0.9}
                    toScrollWidth={toLeft}
                    onNext={(width: number) => {
                        fetchNextPage();
                    }}
                >
                    <div
                        id={`decomposition-waterfall-infinite-scroll`}
                        className={`flex-1 flex overflow-x-auto max-w-full z-10 ${
                            isFetchingNextPage ? 'opacity-50' : ''
                        }`}
                        ref={chartRef}
                    >
                        {waterFallChartData?.map((_datum: any) => {
                            return (
                                <div
                                    className="hover:bg-black/5"
                                    key={`decomposition-waterfall-chart-${_datum.attributeDesc as string}`}
                                >
                                    <WaterFallChartIndividualised
                                        containerId={`decomposition-waterfall-chart-${_datum.attributeDesc}`}
                                        chartWidth={currentGraphWidth}
                                        chartHeight={chartHeight}
                                        key={`decomposition-waterfall-chart-${_datum.attributeDesc}`}
                                        dataSet={[_datum]}
                                        xAxisField="attributeDesc"
                                        yAxisField="profit_growth_absolute"
                                        colorField="trend"
                                        minBound={waterfallMin}
                                        maxBound={scaleMaxValue(20, waterfallMax)}
                                        toolTipLabel={'Profit Growth'}
                                    />
                                    <p className="text-center">{_datum.attributeDesc}</p>
                                </div>
                            );
                        })}
                    </div>
                </HorizontalInfiniteScroller>

                {/* Current Year Chart */}
                <div className="flex flex-col">
                    <Barchart
                        padding={[0, 0, -8, 0]}
                        barWidth={40}
                        xAxisConfig={true}
                        xAxisLabelFormatter={(val) => {
                            return '';
                        }}
                        dataset={[
                            {
                                brand_name: 'Current Year',
                                totalProfitGrowth: currentYearData ?? 0,
                                type: '#5F49BF',
                            },
                        ]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`decomposition-waterfall-current-year`}
                        fields={['brand_name', 'totalProfitGrowth', 'type']}
                        minBound={waterfallMin}
                        maxBound={scaleMaxValue(20, waterfallMax)}
                        width={100}
                        colorKey={'type'}
                        YGrid={true}
                        yAxisConfig={true}
                        tooltipFormatter={(xAxis, val) => {
                            return {
                                name: 'Profit Growth',
                                value: formatNumWithKMBAlt(val),
                            };
                        }}
                    />
                    <p className="text-center">{'Current Year'}</p>
                </div>
            </div>
        </div>
    );
};

export default DecompositionWaterfallChart;
